import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaSitemap, FaUserTie, FaSearch, FaChartLine, FaPenNib, FaPenFancy,
  FaFilm, FaBolt, FaClock, FaDollarSign, FaLayerGroup, FaCircle, FaMagic,
} from "react-icons/fa";
import {
  colors, shadows, radius, spacing, typography, transitions,
  pageContainer, pageTitle, pageSubtitle, cardBase, btnPrimary, btnSecondary,
} from "../../utils/designTokens";
import KPICard from "../../components/KPICard";
import StatusBadge from "../../components/StatusBadge";
import { correrCicloMarketing, revisarPiezaMarketing, estadoMarketing } from "../../utils/habiApi";

// ── Equipo de Marketing Agéntico ─────────────────────────────────────────────
// Organigrama en vivo de los 6 agentes IA que producen el contenido de Habitia
// (LinkedIn / Instagram / web / SEO / guías) + track comercial. Estilo Habitia
// (claro, tokens del design system). Los datos vienen de Firestore
// (`marketing_agents`) si existen; si no, usa la semilla para renderizar de una.
// Corre como batch diario (~1 h) en Claude Haiku, tope duro US$20/mes.

const ICONOS = {
  director: FaUserTie,
  investigador: FaSearch,
  analista: FaChartLine,
  guionista: FaPenNib,
  redactor: FaPenFancy,
  editor: FaFilm,
};

// Semilla: 6 agentes (Director lidera; los otros 5 le reportan)
const SEMILLA = [
  {
    id: "director", nombre: "Director de Contenido", rol: "Dirección", modelo: "Haiku",
    estado: "trabajando", haciendo: "Ordena los guiones de la semana por ángulo y marca los que ya están para salir.",
    cola: ["Revisar 3 guiones de LinkedIn", "Aprobar reel del jueves", "Cerrar calendario de la semana"],
    reporta_a: "Fefo", tiene_a_cargo: 5,
    resultados: [{ label: "Piezas esta semana", value: 8 }, { label: "Aprobadas", value: 6 }],
  },
  {
    id: "investigador", nombre: "Investigador de Mercado", rol: "Marketing", modelo: "Haiku",
    estado: "trabajando", haciendo: "Revisa reseñas de administradoras y qué dolores se repiten para decidir de qué hablar.",
    cola: ["Escanear reseñas nuevas", "Detectar ángulo de la semana"],
    reporta_a: "Director", tiene_a_cargo: 0,
    resultados: [{ label: "Temas propuestos", value: 4 }, { label: "Con evidencia", value: 4 }],
  },
  {
    id: "analista", nombre: "Analista de Datos", rol: "Marketing", modelo: "Haiku",
    estado: "libre", haciendo: "Cruza el engagement de las piezas con los datos reales de Habitia.",
    cola: ["Medir resultados del reel de la plata"],
    reporta_a: "Director", tiene_a_cargo: 0,
    resultados: [{ label: "Piezas medidas", value: 12 }, { label: "Datos reales usados", value: 9 }],
  },
  {
    id: "guionista", nombre: "Guionista", rol: "Marketing", modelo: "Haiku",
    estado: "esperando_ok", haciendo: "Reescribió el reel del jueves; espera el OK del Director.",
    cola: ["Reescribir hook del reel", "Dejar listo lo de esta semana"],
    reporta_a: "Director", tiene_a_cargo: 0,
    resultados: [{ label: "Guiones listos", value: 5 }, { label: "En revisión", value: 2 }],
  },
  {
    id: "redactor", nombre: "Redactor", rol: "Marketing", modelo: "Haiku",
    estado: "trabajando", haciendo: "Escribe la guía SEO “cuánto cobra una administración” para la web.",
    cola: ["Caption del post de LinkedIn", "Guía SEO", "Copy de material comercial"],
    reporta_a: "Director", tiene_a_cargo: 0,
    resultados: [{ label: "Textos redactados", value: 11 }, { label: "Guías web", value: 2 }],
  },
  {
    id: "editor", nombre: "Editor de Video", rol: "Marketing", modelo: "Haiku",
    estado: "libre", haciendo: "Deja el reel perfecto en el estudio de reels (Remotion).",
    cola: [],
    reporta_a: "Director", tiene_a_cargo: 0,
    resultados: [{ label: "Reels renderizados", value: 7 }, { label: "En cola", value: 0 }],
  },
];

// Layout del organigrama (canvas lógico 780×430): Director arriba centrado, 5 abajo
const CANVAS_W = 780;
const CANVAS_H = 430;
const NODO_W = 150;
const NODO_H = 92;
const DIR_W = 176;
const DIR_H = 84;
const POS = {
  director: { x: CANVAS_W / 2 - DIR_W / 2, y: 14, w: DIR_W, h: DIR_H },
  investigador: { x: 8, y: 286, w: NODO_W, h: NODO_H },
  analista: { x: 8 + (NODO_W + 16), y: 286, w: NODO_W, h: NODO_H },
  guionista: { x: 8 + 2 * (NODO_W + 16), y: 286, w: NODO_W, h: NODO_H },
  redactor: { x: 8 + 3 * (NODO_W + 16), y: 286, w: NODO_W, h: NODO_H },
  editor: { x: 8 + 4 * (NODO_W + 16), y: 286, w: NODO_W, h: NODO_H },
};

const EquipoMarketingAgentico = () => {
  const [agentes, setAgentes] = useState(SEMILLA);
  const [selId, setSelId] = useState("director");
  const [presupuesto, setPresupuesto] = useState({ usado: 0, tope: 20 });
  const [pendientes, setPendientes] = useState([]);
  const [aprobadas, setAprobadas] = useState([]);
  const [reportes, setReportes] = useState([]);
  const [foco, setFoco] = useState("");
  const [corriendo, setCorriendo] = useState(false);
  const [copiado, setCopiado] = useState("");
  const [msg, setMsg] = useState(null); // { tipo: "ok"|"error"|"info", texto }

  // Estado en vivo por el endpoint /api/marketing/status (polling), en vez de
  // onSnapshot de Firestore en el cliente: evita el bug interno del SDK con
  // múltiples listeners (INTERNAL ASSERTION / Unexpected state) y no depende de
  // reglas de lectura del cliente. Si el endpoint no responde, queda la semilla.
  const refrescar = useCallback(async () => {
    try {
      const s = await estadoMarketing();
      if (Array.isArray(s.agents) && s.agents.length) {
        setAgentes(SEMILLA.map((seed) => s.agents.find((a) => a.id === seed.id) || seed));
      }
      if (s.budget) setPresupuesto({ usado: Number(s.budget.used_usd || 0), tope: Number(s.budget.cap_usd || 20) });
      if (Array.isArray(s.pendientes)) setPendientes(s.pendientes);
      if (Array.isArray(s.aprobadas)) setAprobadas(s.aprobadas);
      if (Array.isArray(s.reportes)) setReportes(s.reportes);
    } catch (_) { /* endpoint no disponible: seguimos con la semilla */ }
  }, []);

  useEffect(() => {
    refrescar();
    const t = setInterval(refrescar, 6000);
    return () => clearInterval(t);
  }, [refrescar]);

  const correr = async () => {
    setCorriendo(true);
    setMsg({ tipo: "info", texto: foco.trim() ? "El equipo está trabajando en tu foco… (Haiku)" : "El equipo está trabajando… (Haiku)" });
    try {
      const r = await correrCicloMarketing({ foco: foco.trim() || null, canal: "instagram" });
      if (r.status === "budget_exhausted") {
        setMsg({ tipo: "error", texto: `Se llegó al tope de US$${r.budget?.cap_usd || 20} del mes.` });
      } else if (r.status === "ok" && r.modo === "investigar") {
        setMsg({ tipo: "ok", texto: "Investigación lista — la ves abajo en “Investigaciones”." });
      } else if (r.status === "ok") {
        setMsg({ tipo: "ok", texto: `Listo: ${r.cantidad || 1} pieza(s) para ${r.canal || "instagram"}, esperando tu OK 👇` });
      } else if (r.status === "sin_temas") {
        setMsg({ tipo: "info", texto: "El equipo no encontró un ángulo claro esta vez." });
      } else {
        setMsg({ tipo: "info", texto: "El equipo corrió." });
      }
    } catch (e) {
      setMsg({ tipo: "error", texto: e.message || "No se pudo correr el equipo." });
    } finally {
      setCorriendo(false);
      refrescar();
    }
  };

  const revisar = async (pieza, accion) => {
    setPendientes((xs) => xs.filter((p) => p.id !== pieza.id)); // optimista
    if (accion === "aprobar") setAprobadas((xs) => [{ ...pieza, estado: "aprobado" }, ...xs]);
    try {
      await revisarPiezaMarketing(pieza.id, accion);
      setMsg({ tipo: "ok", texto: accion === "aprobar" ? "Pieza aprobada — quedó lista para publicar 👇" : "Pieza descartada." });
    } catch (e) {
      setMsg({ tipo: "error", texto: e.message || "No se pudo actualizar la pieza." });
    } finally {
      refrescar();
    }
  };

  const copiar = async (pieza) => {
    const texto = [pieza.titulo, "", pieza.copy, pieza.cta ? `\n${pieza.cta}` : "", pieza.hashtags || ""]
      .filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(pieza.id);
      setTimeout(() => setCopiado(""), 1500);
    } catch (_) { /* clipboard no disponible */ }
  };

  const sel = useMemo(() => agentes.find((a) => a.id === selId) || agentes[0], [agentes, selId]);
  const activos = agentes.filter((a) => a.estado === "trabajando").length;
  const enCola = agentes.reduce((s, a) => s + (a.cola?.length || 0), 0);
  const pctPresu = Math.round((presupuesto.usado / presupuesto.tope) * 100);

  const dir = POS.director;
  const hijos = ["investigador", "analista", "guionista", "redactor", "editor"];

  return (
    <div style={pageContainer}>
      {/* Encabezado */}
      <div style={styles.headerRow}>
        <div>
          <h1 style={pageTitle}>
            <FaSitemap style={{ marginRight: 8, verticalAlign: "-2px", color: colors.primary }} />
            Equipo de Marketing
          </h1>
          <p style={pageSubtitle}>
            6 agentes IA producen el contenido de Habitia — batch diario en Haiku, con tu OK antes de publicar.
          </p>
        </div>
        <span style={styles.chipOperativo}>
          <FaCircle style={{ fontSize: 8, color: colors.success }} /> Sistema agéntico operativo
        </span>
      </div>

      {/* Foco del día: le escribís al equipo qué necesitás hoy */}
      <div style={{ ...cardBase, padding: spacing[5], marginBottom: spacing[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: spacing[3] }}>
          <FaBolt style={{ color: colors.primary }} />
          <span style={{ fontSize: 16, color: colors.black }}>El foco de hoy</span>
          <span style={{ fontSize: 12, color: colors.gray600 }}>· escribile al equipo qué necesitás</span>
        </div>
        <textarea
          value={foco}
          onChange={(e) => setFoco(e.target.value)}
          placeholder="Ej: necesito 3 publicaciones para pauta en Instagram · investigá los materiales de los comerciales · mirá qué guías traccionan mejor"
          rows={2}
          style={{
            width: "100%", resize: "vertical", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
            padding: "12px 14px", fontSize: 14, fontFamily: typography.fontFamily, color: colors.gray900,
            background: colors.white, outline: "none", lineHeight: 1.5,
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: spacing[3], marginTop: spacing[3], flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={correr}
            disabled={corriendo}
            style={{ ...btnPrimary, opacity: corriendo ? 0.6 : 1, cursor: corriendo ? "default" : "pointer" }}
          >
            <FaBolt style={{ fontSize: 13 }} /> {corriendo ? "El equipo está trabajando…" : (foco.trim() ? "Ejecutar el foco" : "Correr ciclo (sin foco)")}
          </button>
          <span style={{ fontSize: 12, color: colors.gray400 }}>
            El Director interpreta tu foco y reparte el trabajo. Corre en Haiku; cada ciclo suma al tope de US${presupuesto.tope}/mes.
          </span>
        </div>
      </div>

      {msg && (
        <div style={{
          ...styles.banner,
          background: msg.tipo === "error" ? colors.errorLight : msg.tipo === "ok" ? colors.successLight : colors.primaryLighter,
          color: msg.tipo === "error" ? colors.errorDark : msg.tipo === "ok" ? colors.successDark : colors.primaryDark,
        }}>
          {msg.texto}
        </div>
      )}

      {/* KPIs */}
      <div style={styles.kpiGrid}>
        <KPICard icon={FaLayerGroup} label="Agentes" value={agentes.length} iconBg={colors.primaryLighter} iconColor={colors.primary} />
        <KPICard icon={FaBolt} label="Trabajando ahora" value={activos} iconBg={colors.infoLight} iconColor={colors.infoDark} />
        <KPICard icon={FaClock} label="Tareas en cola" value={enCola} iconBg={colors.warningLight} iconColor={colors.warningDark} />
        <KPICard
          icon={FaDollarSign} label="Presupuesto del mes" value={`US$${presupuesto.usado} / ${presupuesto.tope}`}
          iconBg={colors.successLight} iconColor={colors.successDark}
          badge={`${pctPresu}%`} badgeColor={pctPresu >= 90 ? "error" : pctPresu >= 70 ? "warning" : "success"}
        />
      </div>

      {/* Cuerpo: organigrama + panel de detalle */}
      <div style={styles.body}>
        {/* Organigrama */}
        <div style={{ ...cardBase, flex: 1, minWidth: 0, padding: spacing[5] }}>
          <div style={styles.orgTitle}>Quién está trabajando ahora.</div>
          <div style={styles.orgScroll}>
            <div style={{ position: "relative", width: CANVAS_W, height: CANVAS_H, margin: "0 auto" }}>
              {/* Conexiones */}
              <svg width={CANVAS_W} height={CANVAS_H} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                {hijos.map((hid) => {
                  const c = POS[hid];
                  const x1 = dir.x + dir.w / 2, y1 = dir.y + dir.h;
                  const x2 = c.x + c.w / 2, y2 = c.y;
                  const mid = (y1 + y2) / 2;
                  const activo = (agentes.find((a) => a.id === hid) || {}).estado === "trabajando";
                  return (
                    <path
                      key={hid}
                      d={`M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`}
                      fill="none"
                      stroke={activo ? colors.primary : colors.gray300}
                      strokeWidth={activo ? 2 : 1.5}
                      opacity={activo ? 0.9 : 0.6}
                    />
                  );
                })}
              </svg>

              {/* Nodo Director */}
              <AgentNode
                agente={agentes.find((a) => a.id === "director")}
                pos={dir} big selected={selId === "director"} onClick={() => setSelId("director")}
              />
              {/* Nodos hijos */}
              {hijos.map((hid) => (
                <AgentNode
                  key={hid}
                  agente={agentes.find((a) => a.id === hid)}
                  pos={POS[hid]} selected={selId === hid} onClick={() => setSelId(hid)}
                />
              ))}
            </div>
          </div>
          <div style={styles.orgHint}>
            Flujo: <b style={{ color: colors.gray900 }}>Investigador → Director → Guionista → Redactor · Analista → Editor</b> · el Director ordena y vos das el OK final.
          </div>
        </div>

        {/* Panel de detalle */}
        <AgentDetailPanel agente={sel} />
      </div>

      {/* Piezas esperando tu OK (checkpoint humano) */}
      {pendientes.length > 0 && (
        <div style={{ ...cardBase, marginTop: spacing[5], padding: spacing[5] }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: spacing[4] }}>
            <span style={styles.orgTitle}>Esperando tu OK</span>
            <StatusBadge status="esperando_ok" label={`${pendientes.length} pieza${pendientes.length > 1 ? "s" : ""}`} size="sm" />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
            {pendientes.map((p) => (
              <div key={p.id} style={styles.pieza}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={styles.canalChip}>{p.canal || "—"}</span>
                    {(p.entregable || p.formato) && <span style={styles.fmtChip}>{p.entregable || p.formato}</span>}
                    <span style={{ fontSize: 14, color: colors.black }}>{p.titulo || "(sin título)"}</span>
                  </div>
                  <div style={{ fontSize: 13, color: colors.gray600, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {(p.copy || "").slice(0, 280)}{(p.copy || "").length > 280 ? "…" : ""}
                  </div>
                  {p.dato_real && (
                    <div style={{ fontSize: 11, color: colors.gray400, marginTop: 6 }}>Dato: {p.dato_real}</div>
                  )}
                  {(p.para_editor || p.formato === "reel" || p.formato === "carrusel") && (
                    <div style={{ marginTop: 8, fontSize: 12, color: colors.primary, background: colors.primaryLighter, borderRadius: radius.sm, padding: "6px 10px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      💡 Idea + guion para el <b>Editor de Reels</b> (el video lo armás ahí)
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                  <button type="button" style={{ ...btnPrimary, fontSize: 13, justifyContent: "center" }} onClick={() => revisar(p, "aprobar")}>Aprobar</button>
                  <button type="button" style={{ ...btnSecondary, fontSize: 13, justifyContent: "center" }} onClick={() => revisar(p, "descartar")}>Descartar</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Listas para publicar (aprobadas) — el humano las publica */}
      {aprobadas.length > 0 && (
        <div style={{ ...cardBase, marginTop: spacing[5], padding: spacing[5] }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: spacing[4] }}>
            <span style={styles.orgTitle}>Listas para publicar</span>
            <StatusBadge status="aprobado" label={`${aprobadas.length}`} size="sm" />
            <span style={{ fontSize: 12, color: colors.gray600 }}>· aprobadas, listas para que las publiques</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
            {aprobadas.map((p) => (
              <div key={p.id} style={styles.pieza}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={styles.canalChip}>{p.canal || "—"}</span>
                    {(p.entregable || p.formato) && <span style={styles.fmtChip}>{p.entregable || p.formato}</span>}
                    <span style={{ fontSize: 14, color: colors.black }}>{p.titulo || "(sin título)"}</span>
                  </div>
                  <div style={{ fontSize: 13, color: colors.gray600, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {(p.copy || "").slice(0, 320)}{(p.copy || "").length > 320 ? "…" : ""}
                  </div>
                  {p.hashtags && <div style={{ fontSize: 12, color: colors.primary, marginTop: 6 }}>{p.hashtags}</div>}
                  {(p.para_editor || p.formato === "reel" || p.formato === "carrusel") && !p.render_tipo && (
                    <div style={{ marginTop: 8, fontSize: 12, color: colors.primary, background: colors.primaryLighter, borderRadius: radius.sm, padding: "6px 10px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      💡 Idea + guion para el <b>Editor de Reels</b> — copiá y armá el {p.formato === "carrusel" ? "carrusel" : "reel"} ahí (queda mejor a mano)
                    </div>
                  )}
                  {p.render_tipo && (
                    p.render_estado === "listo" && p.render_url ? (
                      <video src={p.render_url} controls playsInline style={{ marginTop: 10, maxHeight: 360, borderRadius: 8, background: "#000", display: "block" }} />
                    ) : p.render_estado === "listo" && Array.isArray(p.render_urls) && p.render_urls.length ? (
                      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginTop: 10, paddingBottom: 4 }}>
                        {p.render_urls.map((u, k) => <img key={k} src={u} alt={`slide ${k + 1}`} style={{ height: 220, borderRadius: 8, flex: "none", boxShadow: shadows.sm }} />)}
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, fontSize: 12, color: colors.warningDark, background: colors.warningLight, borderRadius: radius.sm, padding: "6px 10px", display: "inline-block" }}>
                        🎬 {p.render_estado === "renderizando" ? "Renderizando en el estudio…" : `${p.render_tipo === "reel" ? "Reel" : "Carrusel"} en cola de render — abrí el estudio de reels para procesarlo`}
                      </div>
                    )
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    style={{ ...(copiado === p.id ? { ...btnPrimary, background: colors.success } : btnSecondary), fontSize: 13, justifyContent: "center" }}
                    onClick={() => copiar(p)}
                  >
                    {copiado === p.id ? "¡Copiado!" : "Copiar"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Investigaciones (reportes del equipo) */}
      {reportes.length > 0 && (
        <div style={{ ...cardBase, marginTop: spacing[5], padding: spacing[5] }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: spacing[4] }}>
            <FaSearch style={{ color: colors.primary }} />
            <span style={styles.orgTitle}>Investigaciones</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            {reportes.map((r) => (
              <div key={r.id} style={{ ...styles.pieza, flexDirection: "column", alignItems: "stretch" }}>
                <div style={{ fontSize: 14, color: colors.black, marginBottom: 6 }}>{r.titulo}</div>
                {r.copy && <div style={{ fontSize: 13, color: colors.gray900, lineHeight: 1.5, marginBottom: 8 }}>{r.copy}</div>}
                {Array.isArray(r.hallazgos) && r.hallazgos.length > 0 && (
                  <>
                    <div style={styles.repLabel}>Hallazgos</div>
                    <ul style={styles.repList}>{r.hallazgos.map((h, i) => <li key={i} style={styles.repItem}>{h}</li>)}</ul>
                  </>
                )}
                {Array.isArray(r.recomendaciones) && r.recomendaciones.length > 0 && (
                  <>
                    <div style={styles.repLabel}>Recomendaciones</div>
                    <ul style={styles.repList}>{r.recomendaciones.map((h, i) => <li key={i} style={styles.repItem}>{h}</li>)}</ul>
                  </>
                )}
                {Array.isArray(r.faltan_datos) && r.faltan_datos.length > 0 && (
                  <div style={{ fontSize: 12, color: colors.warningDark, background: colors.warningLight, borderRadius: radius.sm, padding: "8px 10px", marginTop: 6 }}>
                    Para profundizar falta conectar: {r.faltan_datos.join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Nodo del organigrama ─────────────────────────────────────────────────────
const AgentNode = ({ agente, pos, big, selected, onClick }) => {
  if (!agente) return null;
  const Icon = ICONOS[agente.id] || FaUserTie;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: "absolute", left: pos.x, top: pos.y, width: pos.w, minHeight: pos.h,
        textAlign: "left", cursor: "pointer",
        background: colors.bgCard, borderRadius: radius.md,
        border: `1.5px solid ${selected ? colors.primary : "transparent"}`,
        boxShadow: selected ? shadows.md : shadows.sm,
        padding: `${spacing[3]}px ${spacing[3]}px`,
        display: "flex", flexDirection: "column", gap: 6,
        fontFamily: typography.fontFamily, transition: `all ${transitions.fast}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          width: big ? 30 : 26, height: big ? 30 : 26, borderRadius: radius.sm, flexShrink: 0,
          background: colors.primaryLighter, color: colors.primary,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon style={{ fontSize: big ? 15 : 13 }} />
        </span>
        <span style={{ fontSize: big ? 15 : 13, color: colors.black, lineHeight: 1.15 }}>
          {agente.id === "director" ? "Director" : agente.nombre.split(" ")[0]}
        </span>
        {agente.estado === "trabajando" && <PulsoActivo />}
      </div>
      <StatusBadge status={agente.estado} variant="dot" size="sm" />
      {big && (
        <span style={{ fontSize: 11, color: colors.gray600, lineHeight: 1.35 }}>
          {agente.haciendo}
        </span>
      )}
    </button>
  );
};

const PulsoActivo = () => (
  <span style={{ marginLeft: "auto", position: "relative", width: 8, height: 8 }}>
    <span style={{
      position: "absolute", inset: 0, borderRadius: "50%", background: colors.primary,
      animation: "habitiaPulse 1.6s ease-out infinite",
    }} />
    <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: colors.primary }} />
    <style>{`@keyframes habitiaPulse {0%{transform:scale(1);opacity:.7}70%{transform:scale(2.6);opacity:0}100%{opacity:0}}`}</style>
  </span>
);

// ── Panel de detalle del agente seleccionado ─────────────────────────────────
const AgentDetailPanel = ({ agente }) => {
  if (!agente) return null;
  const Icon = ICONOS[agente.id] || FaUserTie;
  return (
    <div style={{ ...cardBase, width: 340, flexShrink: 0, padding: spacing[5], alignSelf: "flex-start" }}>
      <div style={styles.detLabel}>Agente seleccionado</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0 4px" }}>
        <span style={{
          width: 38, height: 38, borderRadius: radius.sm, background: colors.primaryLighter, color: colors.primary,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon style={{ fontSize: 18 }} />
        </span>
        <div>
          <div style={{ fontSize: 16, color: colors.black, lineHeight: 1.1 }}>{agente.nombre}</div>
          <div style={{ fontSize: 11, color: colors.gray600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {agente.rol}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "10px 0 14px" }}>
        <StatusBadge status={agente.estado} size="sm" />
        <span style={styles.modeloChip}>Modelo · {agente.modelo}</span>
      </div>

      <div style={styles.detLabel}>Qué está haciendo ahora</div>
      <div style={styles.haciendoCard}>{agente.haciendo}</div>

      <div style={styles.metaRow}>
        <div><div style={styles.metaK}>Reporta a</div><div style={styles.metaV}>{agente.reporta_a}</div></div>
        <div><div style={styles.metaK}>Tiene a cargo</div><div style={styles.metaV}>{agente.tiene_a_cargo} agentes</div></div>
      </div>

      <div style={{ ...styles.detLabel, marginTop: 14 }}>Su cola de hoy</div>
      {agente.cola?.length ? (
        <ul style={styles.colaList}>
          {agente.cola.map((t, i) => (
            <li key={i} style={styles.colaItem}>
              <FaCircle style={{ fontSize: 5, color: colors.gray400, marginRight: 8, verticalAlign: "middle" }} />
              {t}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ fontSize: 13, color: colors.gray400, padding: "6px 0" }}>Sin tareas en cola.</div>
      )}

      <div style={{ ...styles.detLabel, marginTop: 14 }}>Resultados</div>
      <div style={styles.resGrid}>
        {agente.resultados?.map((r, i) => (
          <div key={i} style={styles.resCard}>
            <div style={styles.resValue}>{r.value}</div>
            <div style={styles.resLabel}>{r.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button type="button" style={{ ...btnSecondary, flex: 1, justifyContent: "center", fontSize: 13 }} disabled>
          <FaMagic style={{ fontSize: 12 }} /> Pulir con Sonnet
        </button>
      </div>
      <div style={{ fontSize: 11, color: colors.gray400, marginTop: 8, lineHeight: 1.4 }}>
        Corre en el batch diario (~1 h). Escalar a Sonnet gasta del tope mensual.
      </div>
    </div>
  );
};

const styles = {
  headerRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: spacing[4], flexWrap: "wrap", marginBottom: spacing[6] },
  chipOperativo: {
    display: "inline-flex", alignItems: "center", gap: 8, background: colors.successLight, color: colors.successDark,
    border: `1px solid #86efac`, borderRadius: radius.full, padding: "6px 14px", fontSize: 12, whiteSpace: "nowrap",
  },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: spacing[4], marginBottom: spacing[6] },
  body: { display: "flex", gap: spacing[5], alignItems: "flex-start", flexWrap: "wrap" },
  orgTitle: { fontSize: typography.sizes["2xl"], color: colors.black, marginBottom: spacing[4] },
  orgScroll: { overflowX: "auto", paddingBottom: spacing[2] },
  orgHint: { fontSize: 12, color: colors.gray600, marginTop: spacing[4], lineHeight: 1.5 },
  detLabel: { fontSize: 11, color: colors.gray400, textTransform: "uppercase", letterSpacing: "0.6px" },
  modeloChip: { fontSize: 11, color: colors.gray700, background: colors.gray100, borderRadius: radius.sm, padding: "3px 8px" },
  haciendoCard: { fontSize: 13.5, color: colors.gray900, background: colors.gray50, borderRadius: radius.sm, padding: "10px 12px", margin: "6px 0 14px", lineHeight: 1.45 },
  metaRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3] },
  metaK: { fontSize: 11, color: colors.gray400, textTransform: "uppercase", letterSpacing: "0.5px" },
  metaV: { fontSize: 14, color: colors.gray900, marginTop: 2 },
  colaList: { listStyle: "none", margin: "6px 0 0", padding: 0 },
  colaItem: { fontSize: 13, color: colors.gray900, padding: "4px 0", lineHeight: 1.4 },
  resGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[3], marginTop: 6 },
  resCard: { background: colors.gray50, borderRadius: radius.sm, padding: "10px 12px" },
  resValue: { fontSize: typography.sizes["2xl"], color: colors.primary, lineHeight: 1.1 },
  resLabel: { fontSize: 11, color: colors.gray600, marginTop: 2 },
  banner: { borderRadius: radius.sm, padding: "10px 14px", fontSize: 13.5, marginBottom: spacing[5] },
  pieza: { display: "flex", gap: spacing[4], alignItems: "flex-start", background: colors.gray50, borderRadius: radius.md, padding: spacing[4] },
  canalChip: { fontSize: 10.5, color: colors.primary, background: colors.primaryLighter, borderRadius: radius.sm, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.5px" },
  fmtChip: { fontSize: 10.5, color: colors.gray700, background: colors.gray100, borderRadius: radius.sm, padding: "2px 8px", textTransform: "uppercase", letterSpacing: "0.5px" },
  repLabel: { fontSize: 11, color: colors.gray400, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 6 },
  repList: { margin: "4px 0 4px", paddingLeft: 18 },
  repItem: { fontSize: 13, color: colors.gray900, lineHeight: 1.5, marginBottom: 3 },
};

export default EquipoMarketingAgentico;
